import { CSSProperties, useState } from 'react';

import { AppIcon, formatCurrency } from '@backrs_ui/common-ui';

import { Box, Typography, ClickAwayListener, Tooltip, SxProps, Theme } from '@mui/material';

type InvestmentCardProps = Readonly<{
  title: string;
  tooltipText: string;
  value: number;
}>;

export const InvestmentCard = ({ title, tooltipText, value }: InvestmentCardProps) => {
  const [isMuiTooltipOpen, setIsMuiTooltipOpen] = useState(false);

  return (
    <Box sx={cardSx}>
      <Box sx={cardHeaderSx}>
        <Typography
          variant="text-sm-semibold"
          color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        >
          {title}
        </Typography>

        <ClickAwayListener onClickAway={() => setIsMuiTooltipOpen(false)}>
          <Tooltip
            open={isMuiTooltipOpen}
            onClose={() => setIsMuiTooltipOpen(false)}
            placement="top"
            arrow
            disableFocusListener
            disableHoverListener
            disableTouchListener
            sx={tooltipSx}
            componentsProps={{
              tooltip: {
                sx: toolTipComponentSx,
              },
            }}
            title={tooltipText}
          >
            <Box sx={tooltipIconSx} onClick={() => setIsMuiTooltipOpen((prev) => !prev)}>
              <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
            </Box>
          </Tooltip>
        </ClickAwayListener>
      </Box>

      <Typography variant="display-sm-semibold">{formatCurrency(value)}</Typography>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
  gap: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const cardHeaderSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const tooltipIconSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const tooltipSx: SxProps<Theme> = {
  fontFamily: 'Sarabun, sans-serif',
  whiteSpace: 'pre-line',
  '& .MuiTooltip-tooltip': {
    fontFamily: 'Sarabun, sans-serif',
    whiteSpace: 'pre-line',
  },
};

const toolTipComponentSx: CSSProperties = {
  fontFamily: 'Sarabun, sans-serif',
  whiteSpace: 'pre-line',
};
